import { useEffect, useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { arcaInvoiceStatus, dateLabel, money, num } from '../../lib/format';
import type { ArcaInvoice, ArcaInvoicePayload, ListMeta } from '../../lib/types';
import { AttachmentPanel } from './AttachmentPanel';
import { FinanceGateNotice, isFinanceGateDisabled, useFinanceGate } from './FinanceGate';

// ARCA 税票页面（I02 只读基线）。
//
// 三处刻意留空，避免把未定口径做成既成事实：
//   · 部分开票（partially_invoiced）：合同 USD × 开票汇率 ↔ 发票 ARS 的核对规则待财务确认，
//     core-api 亦未实现（app.js 明文预留），本页不做推算；
//   · 已授权票据的更正/取消：Factura 侧走 ARCA 更正流程，本地不得抹除税务授权；
//   · 电子支票到账：不把「已登记收款」当成「已到账」。
function InvoiceDetail({ row }: { row: ArcaInvoice }) {
  const { user } = useAuth();
  const { data, loading, error } = useDetail<ArcaInvoice>(`/api/arca-invoices/${row.id}`);
  const invoice = data ?? row;

  const [payloads, setPayloads] = useState<ArcaInvoicePayload[]>([]);
  const [payloadMeta, setPayloadMeta] = useState<ListMeta | null>(null);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [openPayload, setOpenPayload] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<{ data: ArcaInvoicePayload[]; meta: ListMeta }>(`/api/arca-invoices/${row.id}/payloads`);
        if (cancelled) return;
        setPayloads(body.data || []);
        setPayloadMeta(body.meta || null);
        setPayloadError(null);
      } catch (e) {
        if (cancelled) return;
        if (isFinanceGateDisabled(e)) setPayloadError('本环境未启用原始报文留存');
        else setPayloadError(e instanceof ApiError ? e.message : '原始报文读取失败');
        setPayloads([]);
      }
    })();
    return () => { cancelled = true; };
  }, [row.id]);

  const st = arcaInvoiceStatus(invoice.status);

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{invoice.internal_number}</span>
        <h3>{invoice.customer_name || invoice.customer_id}</h3>
        <Pill tone={st.tone} label={st.label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}

      <div className="kv-grid">
        <KeyValue k="票种">{invoice.voucher_type}</KeyValue>
        <KeyValue k="销售点 / 凭证号">{`${invoice.point_of_sale || '—'} · ${invoice.voucher_number ?? '—'}`}</KeyValue>
        <KeyValue k="开票日期">{dateLabel(invoice.invoice_date)}</KeyValue>
        <KeyValue k="到期日">{dateLabel(invoice.due_date)}</KeyValue>
        <KeyValue k="合同">{invoice.contract_number || invoice.contract_id || '—'}</KeyValue>
        <KeyValue k="客户税号">{invoice.customer_tax_id || '—'}</KeyValue>
        <KeyValue k="币种 / 汇率">{`${invoice.currency} · ${num(invoice.exchange_rate, 6)}`}</KeyValue>
        <KeyValue k="净额 / 税额">{`${money(invoice.net_total, invoice.currency)} / ${money(invoice.tax_total, invoice.currency)}`}</KeyValue>
        <KeyValue k="总额（原币）">{money(invoice.grand_total, invoice.currency)}</KeyValue>
        <KeyValue k="总额（ARS）">{money(invoice.grand_total_ars, 'ARS')}</KeyValue>
        <KeyValue k="已核销收款">{money(invoice.paid_amount, 'ARS')}</KeyValue>
        <KeyValue k="未收余额">{money(invoice.outstanding_amount, 'ARS')}</KeyValue>
        <KeyValue k="CAE">{invoice.cae || '—'}</KeyValue>
        <KeyValue k="CAE 到期">{dateLabel(invoice.cae_due_date)}</KeyValue>
        <KeyValue k="来源">{`${invoice.source_system || '—'} · ${invoice.source_id || '—'}`}</KeyValue>
        <KeyValue k="二维码">{invoice.qr_url || '—'}</KeyValue>
      </div>

      <div className="muted" style={{ fontSize: 12.5, margin: '6px 0 12px' }}>
        未收余额 = ARS 总额 − 已登记收款核销额。它**不代表**「已到账/未到账」——
        电子支票等收款方式的实际到账日期与状态尚未在本系统落库（待财务确认口径）。
        部分开票金额判定、已授权票据的更正与取消同样未实现，本页不作推算。
      </div>

      <PayloadSection
        payloads={payloads} meta={payloadMeta} error={payloadError}
        openId={openPayload} onToggle={setOpenPayload}
      />

      <div style={{ fontWeight: 650, marginBottom: 6 }}>附件（原件登记）</div>
      <AttachmentPanel entityType="arca_invoice" entityId={invoice.id} canUpload={can(user, 'attachments', 'create')} />
    </div>
  );
}
// 原始报文列表：同一张发票可能有多份 draft/result（历史版本不被覆盖），
// 因此这里按「收到时间」逐条列出，而不是只显示最后一版。
function PayloadSection({ payloads, meta, error, openId, onToggle }: {
  payloads: ArcaInvoicePayload[];
  meta: ListMeta | null;
  error: string | null;
  openId: number | null;
  onToggle: (id: number | null) => void;
}) {
  const opened = payloads.find((p) => p.id === openId) || null;
  let openedJson = '';
  if (opened) {
    try { openedJson = JSON.stringify(JSON.parse(opened.payload_json), null, 2); }
    catch { openedJson = opened.payload_json; }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 650, marginBottom: 6 }}>
        原始报文留存 {meta ? `（${meta.total} 条）` : ''}
      </div>
      {error ? <div className="notice warn">{error}</div> : payloads.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5 }}>暂无原始报文记录。</div>
      ) : (
        <table className="lines">
          <thead>
            <tr>
              <th>类型</th><th>来源编号</th><th>收到时间</th><th>sha256（前16位）</th>
              <th style={{ textAlign: 'right' }}>原文</th>
            </tr>
          </thead>
          <tbody>
            {payloads.map((p) => (
              <tr key={p.id}>
                <td>{p.kind === 'draft' ? '草稿（draft）' : '授权结果（result）'}</td>
                <td>{p.source_id}</td>
                <td>{String(p.received_at).slice(0, 19)}</td>
                <td className="num">{String(p.sha256).slice(0, 16)}…</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn" onClick={() => onToggle(openId === p.id ? null : p.id)}>
                    {openId === p.id ? '收起' : '查看'}
                  </button>
                </td>
              </tr>
            ))}
            {opened ? (
              <tr>
                <td colSpan={5}>
                  <pre style={{ maxHeight: 260, overflow: 'auto', fontSize: 12 }}>{openedJson}</pre>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * 页面入口。
 *
 * 门控状态统一走 useFinanceGate()（与导航、收款页同源），**不用本页端点自己探**：
 * 门控关闭时 GET /api/arca-invoices 为了向后兼容仍返回旧信封 200，
 * 拿它探会把「本环境未启用」误判成「已启用」，于是用户看到一张空表还以为功能坏了。
 */
export function ArcaInvoicesPage() {
  const gate = useFinanceGate();
  if (gate === 'disabled') return <FinanceGateNotice />;
  if (gate !== 'enabled') return <Spinner label="正在确认本环境财务能力…" />;

  const columns: Column<ArcaInvoice>[] = [
    { header: '内部编号', render: (r) => <span className="num">{r.internal_number}</span> },
    { header: '票种', render: (r) => r.voucher_type },
    { header: '销售点·凭证号', render: (r) => `${r.point_of_sale || '—'}·${r.voucher_number ?? '—'}` },
    { header: '开票日期', render: (r) => dateLabel(r.invoice_date) },
    { header: '客户', render: (r) => r.customer_name || r.customer_id },
    { header: '合同', render: (r) => r.contract_number || '—' },
    { header: '汇率', align: 'right', render: (r) => num(r.exchange_rate, 4) },
    { header: '总额 ARS', align: 'right', render: (r) => money(r.grand_total_ars, 'ARS') },
    { header: '已核销', align: 'right', render: (r) => money(r.paid_amount, 'ARS') },
    { header: '未收余额', align: 'right', render: (r) => money(r.outstanding_amount, 'ARS') },
    { header: '状态', render: (r) => { const s = arcaInvoiceStatus(r.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <ListPage<ArcaInvoice>
      title="ARCA 税票"
      desc="已授权发票登记与原始报文留存（只读基线）。金额为发票 ARS 口径，与合同 USD 金额分开保存。"
      endpoint="/api/arca-invoices"
      rowId={(r) => r.id}
      columns={columns}
      renderDetail={(row) => <InvoiceDetail row={row} />}
      searchPlaceholder="搜索内部编号 / CAE / 凭证号 / 客户 / 合同号…"
    />
  );
}